import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import axios from "./axios";

/**
 * Fleet reports — client-side API layer.
 *
 * Two operations are exposed:
 *  - useAvailableReportSections(fleetId): the list of section identifiers
 *    the backend knows how to generate. Used by the modal to render its
 *    checklist without hardcoding identifiers (which would drift as the
 *    backend adds new sections).
 *  - useGenerateFleetReport(fleetId): posts the user's selection and triggers
 *    a browser download of the resulting XLSX / CSV.
 *
 * The download is done here rather than in the component so the component
 * only owns UI concerns. Blob handling in React is fiddly enough that
 * centralizing it in the hook removes a common source of bugs (leaked object
 * URLs, missing filename, wrong MIME).
 */

/** Section identifiers accepted by POST /fleets/{fleetId}/reports/generate. */
export type FleetReportSectionKey =
  | "fleet_overview"
  | "vehicles_by_status"
  | "incidents"
  | "costs"
  | "contracts_ending"
  | "energy_mix";

export type FleetReportFormat = "xlsx" | "csv";

export interface GenerateFleetReportPayload {
  format: FleetReportFormat;
  sections: FleetReportSectionKey[];
  /** Optional lower bound (YYYY-MM-DD) — filters the incidents section. */
  date_from?: string;
  /** Optional upper bound (YYYY-MM-DD) — filters the incidents section. */
  date_to?: string;
  /** Lookahead window (days) for the contracts_ending section (default 90). */
  contract_ending_days?: number;
}

export const fleetReportKeys = {
  sections: (fleetId: number) =>
    ["fleet", fleetId, "reports", "sections"] as const,
};

/**
 * Fetch the section catalog from the backend. Cached for the session — the
 * catalog is a constant on the server side, so re-fetching would be wasted
 * bandwidth.
 */
export const useAvailableReportSections = (fleetId: number) => {
  const { data, ...rest } = useQuery({
    queryKey: fleetReportKeys.sections(fleetId),
    enabled: !!fleetId,
    // The section list is effectively static; refreshing it on every mount
    // adds no value and adds a request. Set stale time to 30 min.
    staleTime: 30 * 60 * 1000,
    queryFn: async (): Promise<{ sections: FleetReportSectionKey[] }> => {
      const res = await axios.get(`/fleets/${fleetId}/reports/sections`);
      return res.data;
    },
  });

  return {
    sections: data?.sections ?? [],
    ...rest,
  };
};

/**
 * Extract a filename from the Content-Disposition header. The backend sets
 * a well-formed `attachment; filename="..."` string, but this helper stays
 * tolerant of the RFC 5987 `filename*=UTF-8''...` variant that some proxies
 * add. We fall back to a sensible default when the header is missing so
 * the user never ends up with a file called "download".
 */
const filenameFromDisposition = (
  header: string | undefined,
  fallback: string
): string => {
  if (!header) return fallback;
  // Prefer RFC 5987 UTF-8 filename* if present (survives non-ASCII).
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      /* fall through */
    }
  }
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain?.[1] ?? fallback;
};

/**
 * Trigger the browser download of a Blob. Object URLs must be revoked or
 * they leak memory; this helper guarantees revocation even if the click
 * throws (rare but possible on locked-down enterprise browsers).
 */
const triggerDownload = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    // Some browsers (Safari) require the anchor to be in the DOM to accept
    // the programmatic click. Append/click/remove is the standard dance.
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    // Defer the revoke by a tick: some browsers are still reading the URL
    // when the click event returns, and revoking synchronously can abort
    // the download in Firefox.
    setTimeout(() => window.URL.revokeObjectURL(url), 0);
  }
};

/**
 * Kick off a report generation. Returns a react-query mutation whose
 * mutateAsync(payload) resolves once the file has been offered to the user.
 * Errors surface as toasts using the standard error shape (`response.data.message`).
 */
export const useGenerateFleetReport = (fleetId: number) => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (payload: GenerateFleetReportPayload) => {
      // Axios needs to be told the response is binary; otherwise it tries
      // to JSON.parse the payload and the XLSX file arrives corrupted.
      const res = await axios.post(
        `/fleets/${fleetId}/reports/generate`,
        payload,
        { responseType: "blob" }
      );

      const contentType =
        (res.headers["content-type"] as string | undefined) ??
        (payload.format === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "text/csv");

      const blob = new Blob([res.data], { type: contentType });
      const filename = filenameFromDisposition(
        res.headers["content-disposition"] as string | undefined,
        `fleet-report-${fleetId}.${payload.format}`
      );

      triggerDownload(blob, filename);
      return { filename };
    },
    onSuccess: () => {
      toast.success(
        t("fleetReports.generated", "Report generated successfully")
      );
    },
    onError: async (err: any) => {
      // Because we asked axios for a blob, error bodies also arrive as blobs.
      // Peel them back to a JSON message before showing the toast, otherwise
      // the user sees "[object Blob]".
      let message: string | undefined;
      const errBlob = err?.response?.data;
      if (errBlob instanceof Blob) {
        try {
          const text = await errBlob.text();
          const parsed = JSON.parse(text);
          message = parsed?.message ?? parsed?.error;
        } catch {
          /* leave message undefined; fall back to generic */
        }
      } else {
        message = err?.response?.data?.message;
      }
      toast.error(
        message ??
          t(
            "fleetReports.generateFailed",
            "Could not generate the report. Please try again."
          )
      );
    },
  });
};
